import type {Dispatch, ThunkDispatch, UnknownAction} from '@reduxjs/toolkit';
import {isClient} from '@utils/isClient';
import type {ConfigurationType} from '@api/configuration/api.d';
import {CONFIGURATION_TYPES} from '@api/constants';
import {ACCOUNT_FEATURE_KEYS} from '@constants';
import {loadTranslations} from '@components/appSlice';
import {getIsMemberToken, getIsDelegationToken} from '@utils/jwtUtils';
import {getIsSender} from '@utils/userRole';
import {getIsAccountFeatureActive} from './utils/getIsAccountFeatureActive';
import {getIs2FACompleted} from './2FA/utils';
import {
    fetchUser,
    fetchRecipient,
    initializeSigningRecipient,
    fetchAccount,
    fetchDefaultBrand,
    fetchGroup,
    fetchDelegateUser,
    fetchTransaction,
    fetchAllRecipients,
    fetchAllDocuments,
    fetchDocumentApprovals,
    fetchDocumentFields,
    setApprovalsLoadedForTransaction,
    setFieldsLoadedForTransaction,
    fetchAttachments,
    fetchConfiguration,
    fetchDocumentVisibility,
    fetchVerification,
    fetchAccountProvider,
    fetchAccountFeatures,
    fetchSignature,
    fetchTransactionConditions,
    setIsInitialLoadInProgress,
    clearSignature
} from './signerUiSlice';

type AppDispatch = ThunkDispatch<RootState, undefined, UnknownAction> & Dispatch;

interface LoadDataOptions {
    dispatch: AppDispatch;
    getState: () => any;
    transactionId: string;
    config: any;
    accessToken?: string;
    recipientId?: string | null;
    forceReload?: boolean;
    skipRecipientDependentData?: boolean;
}

interface RootState {
    signerUi: {
        activeAccountId: string | null;
        account: any;
        defaultBrand: any;
        recipient: any;
        transaction: any;
        documents: any[];
        approvals: Record<string, any[]>;
        fields: Record<string, any[]>;
        approvalsLoadedForTransactionId: string | null;
        fieldsLoadedForTransactionId: string | null;
        attachments: any[];
        configurations: Record<string, any>;
        documentVisibility: any[];
        allRecipients: any[];
        allRecipientsTransactionId: string | null;
        user: any;
        isRecipientLoading: boolean;
        isConfigurationLoading: boolean;
        isUserLoading: boolean;
        isAccountLoading: boolean;
        isDefaultBrandLoading: boolean;
        isTransactionLoading: boolean;
        isDocumentsLoading: boolean;
        isApprovalsLoading: boolean;
        isFieldsLoading: boolean;
        isAttachmentsLoading: boolean;
        isDocumentVisibilityLoading: boolean;
        isAllRecipientsLoading: boolean;
        isVerificationLoading: boolean;
        isAccountProviderLoading: boolean;
        isAccountFeaturesLoading: boolean;
        accountFeatures: unknown;
        verification: any;
        accountProvider: any;
    };
    app?: {
        authRedirectUrl?: string | null;
    };
}

/**
 * Unified data loading function for SignerUI
 * Loads data in sequence: recipient -> user -> account -> transaction -> documents -> approvals -> fields
 */
async function loadRecipient(
    dispatch: AppDispatch,
    state: RootState,
    forceReload: boolean,
    isDirectAuth: boolean,
    recipientId?: string | null,
    accessToken?: string
): Promise<boolean> {
    const needsRecipient = !state.signerUi.recipient || forceReload;
    const isLoading = state.signerUi.isRecipientLoading;
    const hasTransaction = !!state.signerUi.transaction;

    if (isLoading) {
        return true;
    }

    if (!hasTransaction) {
        return true;
    }

    if (needsRecipient) {
        const is2FACompleted = isClient && getIs2FACompleted(state.signerUi.transaction?.id, recipientId ?? null);
        const isSender = getIsSender(accessToken);
        const isAlwaysRequireTwoFactorEnabled = getIsAccountFeatureActive(
            state.signerUi.accountFeatures,
            ACCOUNT_FEATURE_KEYS.ALWAYS_REQUIRE_TWO_FACTOR_WHEN_PROVIDED
        );
        const shouldSenderSkipInitialize = isSender && !isAlwaysRequireTwoFactorEnabled;
        const shouldUseInitialize = (isDirectAuth || forceReload) && !is2FACompleted && !shouldSenderSkipInitialize;
        const thunkToUse = shouldUseInitialize ? initializeSigningRecipient : fetchRecipient;
        const result = await dispatch(thunkToUse({accessToken, recipientId: recipientId || undefined}) as any);

        if (result.type.endsWith('/fulfilled')) {
            return true;
        }

        return false;
    }

    return true;
}

async function loadAttachmentsData(
    dispatch: AppDispatch,
    state: RootState,
    forceReload: boolean,
    recipientId?: string | null,
    accessToken?: string
): Promise<boolean> {
    const attachmentsCount = state.signerUi.attachments?.length || 0;
    const needsAttachments = attachmentsCount === 0 || forceReload;
    const isLoading = state.signerUi.isAttachmentsLoading;
    const hasTransaction = !!state.signerUi.transaction;
    const hasRecipient = !!state.signerUi.recipient;

    if (isLoading) {
        return true;
    }

    if (!hasTransaction) {
        return true;
    }

    if (!hasRecipient) {
        return true;
    }

    if (needsAttachments) {
        const result = await dispatch(fetchAttachments({accessToken, recipientId: recipientId || undefined}) as any);

        if (result.type.endsWith('/fulfilled')) {
            return true;
        }

        return false;
    }

    return true;
}

async function loadVerificationData(
    dispatch: AppDispatch,
    state: RootState,
    forceReload: boolean,
    accessToken?: string
): Promise<boolean> {
    const needsVerification = !state.signerUi.verification || forceReload;
    const isLoading = state.signerUi.isVerificationLoading;
    const hasTransaction = !!state.signerUi.transaction;
    const hasRecipient = !!state.signerUi.recipient;

    if (isLoading) {
        return true;
    }

    if (!hasTransaction || !hasRecipient) {
        return true;
    }

    if (needsVerification) {
        const result = await dispatch(fetchVerification({accessToken}) as any);

        if (result.type.endsWith('/fulfilled')) {
            return true;
        }

        return false;
    }

    return true;
}

async function loadAccountProviderData(
    dispatch: AppDispatch,
    state: RootState,
    forceReload: boolean,
    accessToken?: string
): Promise<boolean> {
    const needsAccountProvider = !state.signerUi.accountProvider || forceReload;
    const isLoading = state.signerUi.isAccountProviderLoading;
    const hasVerification = !!state.signerUi.verification;
    const hasActiveAccountId = !!state.signerUi.activeAccountId;

    if (isLoading) {
        return true;
    }

    if (!hasVerification || !hasActiveAccountId) {
        return true;
    }

    if (needsAccountProvider) {
        const result = await dispatch(fetchAccountProvider({accessToken}) as any);

        if (result.type.endsWith('/fulfilled')) {
            return true;
        }

        return false;
    }

    return true;
}

async function loadUserData(
    dispatch: AppDispatch,
    config: any,
    state: RootState,
    forceReload: boolean,
    accessToken?: string
): Promise<boolean> {
    const needsUser = !state.signerUi.activeAccountId || forceReload;
    const isLoading = state.signerUi.isUserLoading;

    if (isLoading) {
        return true;
    }

    if (needsUser) {
        const result = await dispatch(fetchUser({config, accessToken}) as any);

        if (result.type.endsWith('/fulfilled')) {
            return true;
        }

        return false;
    }

    return true;
}

async function loadAccountFeaturesData(
    dispatch: AppDispatch,
    state: RootState,
    forceReload: boolean,
    accessToken?: string
): Promise<boolean> {
    const accountId = state.signerUi.activeAccountId;
    const isLoading = state.signerUi.isAccountFeaturesLoading;
    const hasFeatures = state.signerUi.accountFeatures !== null;
    const needsFeatures = !hasFeatures || forceReload;

    if (isLoading) {
        return true;
    }

    if (!accountId) {
        return true;
    }

    if (needsFeatures) {
        const result = await dispatch(fetchAccountFeatures({accessToken}) as any);

        if (result.type.endsWith('/fulfilled')) {
            return true;
        }

        return false;
    }

    return true;
}

async function loadSignatureData(
    dispatch: AppDispatch,
    getState: () => RootState,
    accessToken?: string
): Promise<boolean> {
    try {
        const state = getState();
        const user = state.signerUi?.user;
        const userId = user?.id;

        dispatch(clearSignature());

        if (!userId) {
            return true;
        }

        const result = await dispatch(fetchSignature({accessToken}) as any);

        if (result.type.endsWith('/fulfilled')) {
            return true;
        }

        return false;
    } catch (error: any) {
        return false;
    }
}

async function loadAccountData(
    dispatch: AppDispatch,
    state: RootState,
    forceReload: boolean,
    accessToken?: string
): Promise<boolean> {
    const needsAccount = !state.signerUi.account || forceReload;
    const isLoading = state.signerUi.isAccountLoading;
    const hasActiveAccountId = !!state.signerUi.activeAccountId;

    if (isLoading) {
        return true;
    }

    if (!hasActiveAccountId) {
        return true;
    }

    if (needsAccount) {
        const result = await dispatch(fetchAccount({accessToken}) as any);

        if (result.type.endsWith('/fulfilled')) {
            return true;
        }

        return false;
    }

    return true;
}

async function loadDefaultBrandData(
    dispatch: AppDispatch,
    state: RootState,
    forceReload: boolean,
    accountId?: string
): Promise<boolean> {
    const needsBrand = !state.signerUi.defaultBrand || forceReload;
    const isLoading = state.signerUi.isDefaultBrandLoading;
    const accountFeatures = state.signerUi.accountFeatures as Array<{key: string; is_active: boolean}> | null;
    const isBrandsFeatureActive = Array.isArray(accountFeatures)
        && accountFeatures.some((feature) => feature.key === 'brands' && feature.is_active);

    console.log('[loadDefaultBrandData] input', {
        accountIdArg: accountId,
        activeAccountIdInState: state.signerUi.activeAccountId,
        existingDefaultBrand: {
            id: state.signerUi?.defaultBrand?.payload?.id,
            name: state.signerUi?.defaultBrand?.payload?.name,
            account_id: state.signerUi?.defaultBrand?.payload?.account_id
        },
        isBrandsFeatureActive
    });

    if (isLoading) {
        return true;
    }

    if (!accountId) {
        console.log('[loadDefaultBrandData] skip: no accountId passed (transaction not loaded yet)');
        return true;
    }

    if (!isBrandsFeatureActive) {
        return true;
    }

    if (needsBrand) {
        const result = await dispatch(fetchDefaultBrand({accountId, useS2SAuth: true}) as any);
        console.log('[loadDefaultBrandData] fetchDefaultBrand result', {
            type: result.type,
            payload: {
                id: result.payload?.id,
                name: result.payload?.name,
                account_id: result.payload?.account_id
            },
            error: result.error,
            meta: result.meta
        });

        if (result.type.endsWith('/fulfilled')) {
            return true;
        }

        return false;
    }

    console.log('[loadDefaultBrandData] skip: already have defaultBrand and no forceReload');
    return true;
}

async function loadTransactionData(
    dispatch: AppDispatch,
    transactionId: string,
    state: RootState,
    forceReload: boolean,
    accessToken?: string
): Promise<boolean> {
    const currentTransactionId = state.signerUi.transaction?.id;
    const needsTransaction = currentTransactionId !== transactionId || forceReload;
    const isLoading = state.signerUi.isTransactionLoading;

    if (isLoading) {
        return true;
    }

    if (needsTransaction) {
        const result = await dispatch(fetchTransaction({transactionId, accessToken}) as any);

        if (result.type.endsWith('/fulfilled')) {
            return true;
        }

        return false;
    }

    return true;
}

async function loadAllRecipientsData(
    dispatch: AppDispatch,
    state: RootState,
    forceReload: boolean,
    accessToken?: string
): Promise<boolean> {
    const {transaction, allRecipients, allRecipientsTransactionId} = state.signerUi;
    const isFaceToFace = !!transaction?.settings?.face_to_face;
    const isLoading = state.signerUi.isAllRecipientsLoading;
    const currentTransactionId = transaction?.id;

    if (isLoading) {
        return true;
    }

    if (!isFaceToFace) {
        return true;
    }

    const hasRecipients = allRecipients?.length > 0;
    const isTransactionMismatch = currentTransactionId !== allRecipientsTransactionId;
    const needsRecipients = !hasRecipients || isTransactionMismatch || forceReload;

    if (needsRecipients) {
        const result = await dispatch(fetchAllRecipients({accessToken}) as any);

        if (result.type.endsWith('/fulfilled')) {
            return true;
        }

        return false;
    }

    return true;
}

async function loadConfigurationData(
    dispatch: AppDispatch,
    state: RootState,
    forceReload: boolean,
    accessToken?: string
): Promise<boolean> {
    const configurationsState = state.signerUi.configurations || {};
    const isLoading = state.signerUi.isConfigurationLoading;
    const hasActiveAccountId = !!state.signerUi.activeAccountId;

    if (isLoading) {
        return true;
    }

    if (!hasActiveAccountId) {
        return true;
    }

    const configurationsToLoad: ConfigurationType[] = [];

    if (!(CONFIGURATION_TYPES.ACCOUNT_SETTINGS in configurationsState) || forceReload) {
        configurationsToLoad.push(CONFIGURATION_TYPES.ACCOUNT_SETTINGS);
    }

    if (!(CONFIGURATION_TYPES.TRANSACTION_SETTINGS in configurationsState) || forceReload) {
        configurationsToLoad.push(CONFIGURATION_TYPES.TRANSACTION_SETTINGS);
    }

    if (configurationsToLoad.length === 0) {
        return true;
    }

    const configurationsPromises = configurationsToLoad.map((configurationType) => dispatch(fetchConfiguration({configurationType, accessToken}) as any)
        .then((result: any) => result)
        .catch((error: any) => ({type: 'fetchConfiguration/rejected', payload: null, error})));

    const results = await Promise.all(configurationsPromises);

    const successful = results.filter((result) => result.type.endsWith('/fulfilled'));
    const hasSuccessful = successful.length > 0;

    return hasSuccessful;
}

async function loadDocumentVisibilityData(
    dispatch: AppDispatch,
    state: RootState,
    forceReload: boolean,
    accessToken?: string
): Promise<boolean> {
    const visibilityCount = state.signerUi.documentVisibility?.length || 0;
    const needsVisibility = visibilityCount === 0 || forceReload;
    const isLoading = state.signerUi.isDocumentVisibilityLoading;
    const hasTransaction = !!state.signerUi.transaction;

    if (isLoading) {
        return true;
    }

    if (!hasTransaction) {
        return true;
    }

    if (needsVisibility) {
        const result = await dispatch(fetchDocumentVisibility({accessToken}) as any);

        if (result.type.endsWith('/fulfilled')) {
            return true;
        }

        return false;
    }

    return true;
}

async function loadDocumentsData(
    dispatch: AppDispatch,
    state: RootState,
    forceReload: boolean,
    accessToken?: string
): Promise<boolean> {
    const documentsCount = state.signerUi.documents.length;
    const needsDocuments = documentsCount === 0 || forceReload;
    const isLoading = state.signerUi.isDocumentsLoading;

    if (isLoading) {
        return true;
    }

    if (needsDocuments) {
        const result = await dispatch(fetchAllDocuments({accessToken}) as any);

        if (result.type.endsWith('/fulfilled')) {
            return true;
        }

        return false;
    }

    return true;
}

function getActiveDocumentId(state: RootState): string | null {
    const path = (typeof window !== 'undefined' && window.location?.pathname)
        || (state as any).app?.requestPath
        || '';

    return path.match(/\/document\/([^/?]+)/)?.[1] || null;
}

function getDocumentIdsToLoad(state: RootState, activeDocumentId: string | null): string[] {
    const documentIds = state.signerUi.documents.map((document: any) => document.id);
    const remainingDocumentIds = documentIds.filter((documentId: string) => documentId !== activeDocumentId);

    return activeDocumentId && documentIds.includes(activeDocumentId)
        ? [activeDocumentId, ...remainingDocumentIds]
        : documentIds;
}

async function loadDocumentApprovalsAndFields(
    dispatch: AppDispatch,
    documentId: string,
    recipientId?: string | null,
    accessToken?: string
): Promise<boolean> {
    const approvalsResult = await dispatch(fetchDocumentApprovals({
        documentId,
        recipientId: recipientId || undefined,
        accessToken
    }) as any).catch((error: any) => ({type: 'fetchDocumentApprovals/rejected', payload: null, error}));

    if (!approvalsResult.type.endsWith('/fulfilled')) {
        return false;
    }

    const fieldsResult = await dispatch(fetchDocumentFields({
        documentId,
        recipientId: recipientId || undefined,
        accessToken
    }) as any).catch((error: any) => ({type: 'fetchDocumentFields/rejected', payload: null, error}));

    return fieldsResult.type.endsWith('/fulfilled');
}

function getIsDocumentApprovalsAndFieldsLoaded(state: RootState, documentId: string): boolean {
    const documentApprovals = state.signerUi.approvals[documentId];

    return documentApprovals !== undefined
        && documentApprovals.every((approval: any) => state.signerUi.fields[approval.id] !== undefined);
}

/** Loads the document the signer is looking at first, then fills in the rest without blocking the render. */
export async function loadApprovalsAndFieldsData(
    dispatch: AppDispatch,
    getState: () => RootState,
    forceReload: boolean,
    recipientId?: string | null,
    accessToken?: string
): Promise<boolean> {
    const state = getState();
    const {transaction} = state.signerUi;
    const transactionId = transaction?.id;
    const activeDocumentId = getActiveDocumentId(state);
    const documentIds = getDocumentIdsToLoad(state, activeDocumentId);
    const documentIdsToLoad = forceReload
        ? documentIds
        : documentIds.filter((documentId) => !getIsDocumentApprovalsAndFieldsLoaded(state, documentId));

    if (documentIdsToLoad.length === 0) {
        return true;
    }

    const [firstDocumentId, ...remainingDocumentIds] = documentIdsToLoad;
    const isFirstDocumentLoaded = await loadDocumentApprovalsAndFields(
        dispatch,
        firstDocumentId,
        recipientId,
        accessToken
    );

    const loadRemainingDocuments = async () => {
        const results = await Promise.all(remainingDocumentIds.map(
            (documentId) => loadDocumentApprovalsAndFields(dispatch, documentId, recipientId, accessToken)
        ));

        if (!isFirstDocumentLoaded || results.some((isLoaded) => !isLoaded)) {
            return;
        }

        dispatch(setApprovalsLoadedForTransaction(transactionId || null));
        dispatch(setFieldsLoadedForTransaction(transactionId || null));
    };

    // SSR ends with the response, so a load left running in the background never reaches the preloaded state
    const canLoadInBackground = typeof window !== 'undefined' && remainingDocumentIds.length > 0;

    if (!canLoadInBackground) {
        await loadRemainingDocuments();
        return isFirstDocumentLoaded;
    }

    loadRemainingDocuments();

    return isFirstDocumentLoaded;
}

/** Loads the recipient's translations in the browser; on the server renderPage sets them from memory. */
async function loadSignerTranslations(
    dispatch: AppDispatch,
    getState: () => RootState,
    forceReload: boolean
): Promise<boolean> {
    if (!isClient) {
        return true;
    }

    const state = getState();
    const {recipient} = state.signerUi;
    const appLocale = (state as any).app?.locale;
    const recipientLanguage = recipient?.language;

    if ((recipientLanguage && recipientLanguage !== appLocale) || forceReload) {
        try {
            await dispatch(loadTranslations(recipientLanguage) as any);
            return true;
        } catch (error: any) {
            return false;
        }
    }

    return true;
}

let loadSignerDataCallCount = 0;

export async function loadSignerData({
    dispatch,
    getState,
    transactionId,
    config,
    accessToken,
    recipientId = null,
    forceReload = false,
    skipRecipientDependentData = false
}: LoadDataOptions): Promise<void> {
    if (!transactionId) {
        return;
    }

    loadSignerDataCallCount += 1;
    const callNumber = loadSignerDataCallCount;
    console.log(`[loadSignerData] call #${callNumber} started`, {
        forceReload, transactionId, recipientId, skipRecipientDependentData
    });

    dispatch(setIsInitialLoadInProgress(true));

    const state = getState();
    const isDirectAuth = !!state.app?.authRedirectUrl;

    const userLoaded = await loadUserData(dispatch, config, state, forceReload, accessToken);
    if (!userLoaded) {
        dispatch(setIsInitialLoadInProgress(false));
        return;
    }

    await loadAccountFeaturesData(dispatch, getState(), forceReload, accessToken);

    await loadSignatureData(dispatch, getState, accessToken);

    const stateAfterUser = getState();
    const accountLoaded = await loadAccountData(dispatch, stateAfterUser, forceReload, accessToken);
    if (!accountLoaded) {
        dispatch(setIsInitialLoadInProgress(false));
        return;
    }

    const transactionLoaded = await loadTransactionData(dispatch, transactionId, getState(), forceReload, accessToken);
    if (!transactionLoaded) {
        dispatch(setIsInitialLoadInProgress(false));
        return;
    }

    const stateAfterTransaction = getState();
    const transactionAccountId = stateAfterTransaction.signerUi.transaction?.account_id;
    await loadDefaultBrandData(dispatch, stateAfterTransaction, forceReload, transactionAccountId);
    await loadConfigurationData(dispatch, stateAfterTransaction, forceReload, accessToken);

    await loadAllRecipientsData(dispatch, stateAfterTransaction, forceReload, accessToken);

    if (!skipRecipientDependentData) {
        await loadRecipient(dispatch, stateAfterTransaction, forceReload, isDirectAuth, recipientId, accessToken);
    }

    const stateAfterRecipientLoad = getState();
    const recipientForGroup = stateAfterRecipientLoad.signerUi.recipient;
    if (!skipRecipientDependentData && accessToken && getIsMemberToken(accessToken)) {
        await dispatch(fetchGroup({accessToken}) as any);
    }

    if (accessToken && getIsDelegationToken(accessToken)) {
        await dispatch(fetchDelegateUser({accessToken}) as any);
    }

    await loadSignerTranslations(dispatch, getState, forceReload);

    if (!skipRecipientDependentData) {
        const stateAfterRecipient = getState();
        await loadAttachmentsData(dispatch, stateAfterRecipient, forceReload, recipientId, accessToken);
        await loadVerificationData(dispatch, stateAfterRecipient, forceReload, accessToken);

        const stateAfterVerification = getState();
        await loadAccountProviderData(dispatch, stateAfterVerification, forceReload, accessToken);
    }

    const stateBeforeVisibility = getState();
    await loadDocumentVisibilityData(dispatch, stateBeforeVisibility, forceReload, accessToken);

    const stateBeforeDocuments = getState();
    const documentsLoaded = await loadDocumentsData(dispatch, stateBeforeDocuments, forceReload, accessToken);
    if (!documentsLoaded) {
        dispatch(setIsInitialLoadInProgress(false));
        return;
    }

    if (!skipRecipientDependentData) {
        await loadApprovalsAndFieldsData(dispatch, getState, forceReload, recipientId, accessToken);
    }

    await dispatch(fetchTransactionConditions({transactionId, accessToken}) as any);

    console.log(`[loadSignerData] call #${callNumber} finished`);
    dispatch(setIsInitialLoadInProgress(false));
}

/**
 * Check what data is missing for a transaction
 * @param {Object} state - Redux state
 * @param {string} transactionId - Transaction ID to check
 * @returns {Object} Object describing missing data
 */
export function getMissingData(state: RootState, transactionId: string): Record<string, any> {
    const {signerUi} = state;

    const missing: Record<string, any> = {};

    if (!signerUi.activeAccountId) {
        missing.needsUser = true;
    }

    if (signerUi.transaction?.id !== transactionId) {
        missing.needsTransaction = true;
    }

    if (!signerUi.recipient) {
        missing.needsRecipient = true;
    }

    if (signerUi.documents.length === 0) {
        missing.needsDocuments = true;
    }

    if (signerUi.approvalsLoadedForTransactionId !== transactionId) {
        missing.needsApprovals = true;
    }

    if (signerUi.fieldsLoadedForTransactionId !== transactionId) {
        missing.needsFields = true;
    }

    return missing;
}
